import dayjs from "dayjs";

export type BillingProvider = "stripe" | "apple";
export type Membership = {
  status: "ready" | "unavailable" | "needs_reconciliation";
  premium: boolean;
  validUntil: string | null;
  providers: BillingProvider[];
  canPurchase: boolean;
  duplicateSubscriptions: boolean;
  subscriptions: Array<{
    id: string; provider: BillingProvider; productId: string; status: string;
    expiresAt: string; autoRenews: boolean; grantsAccess: boolean;
  }>;
};

export const isMembershipCurrent = (membership: Membership | null) =>
  membership?.status === "ready" && !!membership.validUntil && dayjs(membership.validUntil).isAfter(dayjs());

export const parseMembership = (value: unknown): Membership => {
  const membership = value as Membership | null;
  if (!membership || !["ready", "unavailable", "needs_reconciliation"].includes(membership.status)
    || typeof membership.premium !== "boolean" || typeof membership.canPurchase !== "boolean"
    || typeof membership.duplicateSubscriptions !== "boolean"
    || (membership.validUntil !== null && (typeof membership.validUntil !== "string" || !dayjs(membership.validUntil).isValid()))
    || !Array.isArray(membership.providers) || membership.providers.some((provider) => !["stripe", "apple"].includes(provider))
    || !Array.isArray(membership.subscriptions) || membership.subscriptions.some((subscription) => !subscription
      || typeof subscription.id !== "string" || !["stripe", "apple"].includes(subscription.provider)
      || typeof subscription.productId !== "string" || typeof subscription.status !== "string"
      || typeof subscription.expiresAt !== "string" || !dayjs(subscription.expiresAt).isValid() || typeof subscription.autoRenews !== "boolean"
      || typeof subscription.grantsAccess !== "boolean")) {
    throw new Error("Membership response could not be verified. Please retry.");
  }
  return membership;
};

export const redirectToBilling = (value: unknown) => {
  if (typeof value !== "string") throw new Error("Billing could not be opened. Please retry.");
  let url: URL;
  try { url = new URL(value); } catch { throw new Error("Billing returned an invalid destination. Please retry."); }
  const allowedOrigins = new Set(["https://checkout.stripe.com", "https://billing.stripe.com"]);
  for (const entry of (process.env.REACT_APP_BILLING_ALLOWED_ORIGINS || "").split(",").map((origin) => origin.trim()).filter(Boolean)) {
    let configured: URL;
    try { configured = new URL(entry); } catch { throw new Error("Billing destination configuration is invalid."); }
    if (configured.protocol !== "https:" || configured.username || configured.password || configured.pathname !== "/" || configured.search || configured.hash) {
      throw new Error("Billing destination configuration must contain HTTPS origins only.");
    }
    allowedOrigins.add(configured.origin);
  }
  if (url.protocol !== "https:" || !allowedOrigins.has(url.origin) || url.username || url.password) {
    throw new Error("Billing returned an unexpected destination. Please contact support.");
  }
  window.location.assign(url.href);
};
